/**
 * SEO Landing Page - Stillasfirma Oslo (B2B) - GOOGLE ADS OPTIMIZED
 * Professional, trustworthy, keyword-optimized for:
 * - stillas Oslo
 * - stillas for bedrifter
 * - leie stillas Oslo
 * - stillas montering Oslo
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Building2,
  Home,
  CheckCircle2,
  Phone,
  ChevronRight,
  ChevronLeft,
  Shield,
  Users,
  HardHat,
  Clock,
  Send,
  Briefcase,
  Handshake,
  Award,
  FileText,
  MapPin,
  Wrench,
  Camera,
  ImageIcon,
  X
} from "lucide-react";
import InfiniteImageCarousel from "@/components/InfiniteImageCarousel";
import TestimonialsSection from "@/components/TestimonialsSection";
import useScrollTracking from "@/hooks/useScrollTracking";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ============================================
// TRANSLATIONS
// ============================================
const translations = {
  no: {
    // Meta
    pageTitle: "Stillas for Bedrifter i Oslo | Profesjonell Montering | AllStillas",
    pageDescription: "Stillas for bedrifter i Oslo – Profesjonell montering, fleksible løsninger og konkurransedyktige priser. Kontakt oss for tilbud i dag!",
    
    // Nav
    home: "Hjem",
    callNow: "Ring nå",
    navTrustBadge: "20+ års erfaring",
    callNowWithNumber: "Ring nå 93 92 28 19",
    
    // Hero
    forBusinesses: "For bedrifter",
    heroTitle: "Leie stillas i Oslo for bedrifter",
    heroSubtitle: "– Profesjonell montering",
    heroDescription: "Vi leverer stillasløsninger til bedrifter i Oslo og Viken. Kvalitet, sikkerhet og effektivitet – levert av",
    getQuote: "Få tilbud i dag",
    
    // Benefits
    benefit1: "Profesjonell montering på avtalt tid",
    benefit2: "Fleksible leie- og kjøpsløsninger",
    benefit3: "Fokus på sikkerhet og HMS",
    
    // Trust badges
    trustTitle: "Hvorfor velge oss?",
    trust1: "20+ års erfaring i byggebransjen (Doserne AS)",
    trust2: "Lokalt firma i Oslo",
    trust3: "Profesjonell montering",
    trust4: "Vi bruker HAKI og LAYHER stillas",
    trust5: "Fokus på sikkerhet og kvalitet",
    trust6: "Effektiv og ryddig gjennomføring",
    
    // What we offer
    offerTitle: "Hva vi tilbyr bedrifter",
    offerSubtitle: "Vi tilpasser alle oppdrag til dine behov – fra mindre prosjekter til større byggeprosjekter.",
    offer1Title: "Fasadestillas",
    offer1Desc: "Perfekt for fasadearbeid, maling og rehabilitering. Sikkert og stabilt.",
    offer2Title: "Rullestillas",
    offer2Desc: "Fleksible løsninger for innendørs arbeid og mindre prosjekter.",
    offer3Title: "Spesialstillas",
    offer3Desc: "Skreddersydde stillasløsninger for krevende prosjekter.",
    
    // Form
    formTitle: "Be om tilbud for ditt prosjekt",
    formSubtitle: "Fyll ut skjemaet – vi kontakter deg raskt.",
    companyName: "Firmanavn",
    companyPlaceholder: "Ditt firma AS",
    contactPerson: "Kontaktperson",
    contactPlaceholder: "Ola Nordmann",
    projectAddress: "Prosjektadresse",
    addressPlaceholder: "Gateadresse, Oslo",
    projectDescription: "Beskrivelse av prosjektet",
    descriptionPlaceholder: "F.eks: Rehabilitering av fasade, 5-etasjes bygg...",
    phone: "Telefon",
    phonePlaceholder: "+47 xxx xx xxx",
    email: "E-post",
    emailPlaceholder: "firma@epost.no",
    attachFiles: "Legg ved filer (valgfritt)",
    selectFiles: "Velg bilder/PDF",
    maxFiles: "maks 20",
    remaining: "igjen",
    submitButton: "Få tilbud i dag",
    sending: "Sender...",
    thankYou: "Takk for din forespørsel!",
    willContact: "Vi kontakter deg innen kort tid.",
    
    // Projects
    projectsTitle: "Tidligere prosjekter",
    projectsSubtitle: "Vi har levert stillasløsninger til en rekke prosjekter i Oslo – fra mindre oppdrag til større byggeprosjekter.",
    scrollHint: "Scroll eller dra for å se flere",
    contactForRef: "Kontakt oss for referanser fra lignende prosjekter",
    
    // FAQ
    faqTitle: "Ofte stilte spørsmål",
    faq1Q: "Hva koster stillas for bedrifter i Oslo?",
    faq1A: "Pris avhenger av prosjektet – kontakt oss for et uforpliktende tilbud tilpasset dine behov.",
    faq2Q: "Hvor raskt kan dere levere stillas i Oslo?",
    faq2A: "Leveringstid avhenger av prosjektet, men vi tilpasser oss fremdriftsplanen. For akutte behov kan vi ofte levere stillas montering innen få dager.",
    faq3Q: "Jobber dere med større prosjekter?",
    faq3A: "Ja, vi har erfaring med både små og større prosjekter i Oslo og Viken. Vi tilbyr stillas for bedrifter av alle størrelser.",
    faq4Q: "Hvilke stillassystemer bruker dere?",
    faq4A: "Vi bruker profesjonelle systemer som HAKI og LAYHER – bransjens mest pålitelige løsninger for sikker stillas montering.",
    faq5Q: "Kan vi leie stillas i Oslo uten montering?",
    faq5A: "Ja, vi tilbyr både leie av stillas i Oslo med full montering og kun utleie for bedrifter med egne montører.",
    
    // CTA
    ctaTitle: "Klar for å komme i gang?",
    ctaSubtitle: "Kontakt oss i dag for en uforpliktende samtale om ditt prosjekt.",
    ctaPhone: "Ring oss",
    ctaForm: "Til skjema",
    
    // Footer
    footerText: "Stillas for bedrifter i Oslo – levert av",
    allRightsReserved: "Alle rettigheter reservert.",
    forPrivate: "For privat",
    
    // Toasts
    fillRequired: "Vennligst fyll ut kontaktperson og telefon",
    thankYouToast: "Takk! Vi kontakter deg snart.",
    errorToast: "Noe gikk galt. Prøv igjen.",
    uploading: "Laster opp...",
    uploadSuccess: "lastet opp",
    uploadError: "Kunne ikke laste opp",
  },
  en: {
    // Meta
    pageTitle: "Scaffolding for Businesses in Oslo | Professional Assembly | AllStillas",
    pageDescription: "Scaffolding for businesses in Oslo – Professional assembly, flexible solutions and competitive prices. Contact us for a quote today!",
    
    // Nav
    home: "Home",
    callNow: "Call now",
    navTrustBadge: "20+ years experience",
    callNowWithNumber: "Call now 93 92 28 19",
    
    // Hero
    forBusinesses: "For businesses",
    heroTitle: "Rent scaffolding in Oslo for businesses",
    heroSubtitle: "– Professional assembly",
    heroDescription: "We deliver scaffolding solutions to businesses in Oslo and Viken. Quality, safety and efficiency – delivered by",
    getQuote: "Get quote today",
    
    // Benefits
    benefit1: "Professional assembly on agreed time",
    benefit2: "Flexible rental and purchase solutions",
    benefit3: "Focus on safety and HSE",
    
    // Trust badges
    trustTitle: "Why choose us?",
    trust1: "20+ years experience in construction (Doserne AS)",
    trust2: "Local company in Oslo",
    trust3: "Professional assembly",
    trust4: "We use HAKI and LAYHER scaffolding",
    trust5: "Focus on safety and quality",
    trust6: "Efficient and tidy execution",
    
    // What we offer
    offerTitle: "What we offer businesses",
    offerSubtitle: "We customize all assignments to your needs – from smaller projects to larger construction projects.",
    offer1Title: "Facade scaffolding",
    offer1Desc: "Perfect for facade work, painting and rehabilitation. Safe and stable.",
    offer2Title: "Mobile scaffolding",
    offer2Desc: "Flexible solutions for indoor work and smaller projects.",
    offer3Title: "Special scaffolding",
    offer3Desc: "Custom scaffolding solutions for demanding projects.",
    
    // Form
    formTitle: "Request a quote for your project",
    formSubtitle: "Fill out the form – we'll contact you quickly.",
    companyName: "Company name",
    companyPlaceholder: "Your Company Ltd",
    contactPerson: "Contact person",
    contactPlaceholder: "John Smith",
    projectAddress: "Project address",
    addressPlaceholder: "Street address, Oslo",
    projectDescription: "Project description",
    descriptionPlaceholder: "E.g.: Facade rehabilitation, 5-story building...",
    phone: "Phone",
    phonePlaceholder: "+47 xxx xx xxx",
    email: "Email",
    emailPlaceholder: "company@email.com",
    attachFiles: "Attach files (optional)",
    selectFiles: "Select images/PDF",
    maxFiles: "max 20",
    remaining: "remaining",
    submitButton: "Get quote today",
    sending: "Sending...",
    thankYou: "Thank you for your inquiry!",
    willContact: "We will contact you shortly.",
    
    // Projects
    projectsTitle: "Previous projects",
    projectsSubtitle: "We have delivered scaffolding solutions to numerous projects in Oslo – from smaller assignments to larger construction projects.",
    scrollHint: "Scroll or drag to see more",
    contactForRef: "Contact us for references from similar projects",
    
    // FAQ
    faqTitle: "Frequently asked questions",
    faq1Q: "What does scaffolding cost for businesses in Oslo?",
    faq1A: "Price depends on the project – contact us for a non-binding quote tailored to your needs.",
    faq2Q: "How quickly can you deliver scaffolding in Oslo?",
    faq2A: "Delivery time depends on the project, but we adapt to the schedule. For urgent needs, we can often deliver scaffolding assembly within a few days.",
    faq3Q: "Do you work with larger projects?",
    faq3A: "Yes, we have experience with both small and larger projects in Oslo and Viken. We offer scaffolding for businesses of all sizes.",
    faq4Q: "What scaffolding systems do you use?",
    faq4A: "We use professional systems like HAKI and LAYHER – the industry's most reliable solutions for safe scaffolding assembly.",
    faq5Q: "Can we rent scaffolding in Oslo without assembly?",
    faq5A: "Yes, we offer both scaffolding rental in Oslo with full assembly and rental only for companies with their own assemblers.",
    
    // CTA
    ctaTitle: "Ready to get started?",
    ctaSubtitle: "Contact us today for a non-binding conversation about your project.",
    ctaPhone: "Call us",
    ctaForm: "To form",
    
    // Footer
    footerText: "Scaffolding for businesses in Oslo – delivered by",
    allRightsReserved: "All rights reserved.",
    forPrivate: "For private",
    
    // Toasts
    fillRequired: "Please fill in contact person and phone",
    thankYouToast: "Thanks! We'll contact you soon.",
    errorToast: "Something went wrong. Try again.",
    uploading: "Uploading...",
    uploadSuccess: "uploaded",
    uploadError: "Could not upload",
  }
};

export default function StillasfirmaOsloNew() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "no";
  const t = translations[lang];
  
  // Scroll tracking for analytics
  useScrollTracking("b2b");
  
  // Banner state
  const [banner, setBanner] = useState(null);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    project_address: '',
    description: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Image upload state
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Project images state
  const [projectImages, setProjectImages] = useState([]);
  
  // Fetch active banner and project images on mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await fetch(`${API}/api/banner/active`);
        if (res.ok) {
          const data = await res.json();
          if (data.active && data.banner && data.banner.pages?.includes("b2b")) {
            setBanner(data.banner);
          }
        }
      } catch (error) {
        console.debug("Banner fetch failed:", error);
      }
    };
    
    const fetchProjectImages = async () => {
      try {
        // Fetch B2B specific images
        const res = await fetch(`${API}/api/banner/project-images/b2b`);
        if (res.ok) {
          const data = await res.json();
          setProjectImages(data.images || []);
        }
      } catch (error) {
        console.debug("Project images fetch failed:", error);
      }
    };
    
    fetchBanner();
    fetchProjectImages();
  }, []);

  // Image upload handler - max 20 files
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = 20 - images.length;
    if (files.length > remainingSlots) {
      toast.error(lang === "no" 
        ? `Du kan bare laste opp ${remainingSlots} flere bilder (maks 20)` 
        : `You can only upload ${remainingSlots} more images (max 20)`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ${lang === "no" ? "er for stor (maks 10MB)" : "is too large (max 10MB)"}`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${API}/api/projects-v3/public-upload`, {
          method: 'POST',
          body: formDataUpload
        });

        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.secure_url);
          const fileType = file.type.includes('pdf') ? 'PDF' : 'Bilde';
          toast.success(lang === "no" ? `${fileType} ${i + 1}/${files.length} lastet opp` : `${fileType} ${i + 1}/${files.length} uploaded`);
        } else {
          const errorData = await res.json().catch(() => ({}));
          console.error('Upload failed:', res.status, errorData);
          toast.error(lang === "no" ? `Kunne ikke laste opp ${file.name}: ${errorData.detail || 'Ukjent feil'}` : `Could not upload ${file.name}: ${errorData.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(lang === "no" ? `Feil ved opplasting av ${file.name}` : `Error uploading ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      setImages(prev => [...prev, ...uploadedUrls]);
    }
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  // Remove image handler
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contact_person || !formData.phone) {
      toast.error(lang === "no" ? "Vennligst fyll ut kontaktperson og telefon" : "Please fill in contact person and phone");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/projects-v3/stillastjenester/public-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: formData.company_name || formData.contact_person,
          contact_person: formData.contact_person,
          phone: formData.phone,
          email: formData.email || "",
          project_address: formData.project_address || "",
          description: formData.description || "B2B forespørsel fra stillasfirma-oslo siden",
          source_page: "b2b_google_ads",
          images: images
        })
      });
      
      if (res.ok) {
        // Google Ads Conversion Tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18035432072/zLNqCNSS8Y0cEIi1-5dD',
            'value': 1.0,
            'currency': 'NOK'
          });
        }
        setSubmitted(true);
        toast.success(lang === "no" ? "Takk! Vi kontakter deg snart." : "Thanks! We'll contact you soon.");
      } else {
        toast.error(lang === "no" ? "Noe gikk galt. Prøv igjen." : "Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error(lang === "no" ? "Noe gikk galt. Prøv igjen." : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Default placeholder images if none uploaded
  const defaultImages = [
    "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop"
  ];
  
  // Use uploaded images or defaults
  const displayImages = projectImages.length > 0 ? projectImages : defaultImages;

  return (
    <>
      {/* SEO OPTIMIZED META */}
      <Helmet>
        <title>{t.pageTitle}</title>
        <meta name="description" content={t.pageDescription} />
        <meta name="keywords" content="stillas Oslo, stillas for bedrifter, leie stillas Oslo, stillas montering Oslo, stillasfirma Oslo, stillas Viken" />
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="bg-[#23F7DD] text-black font-bold px-2 py-1 rounded text-sm">All</span>
              <span className="text-white font-['Unbounded'] font-bold">Stillas</span>
            </Link>
            
            {/* Trust Badge - Centered */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full">
              <HardHat className="w-4 h-4 text-[#10B981]" />
              <span className="text-[#10B981] text-xs sm:text-sm font-medium">{t.navTrustBadge}</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-[#94A3B8] hover:text-white text-sm flex items-center gap-1 hidden sm:flex">
                <Home className="w-4 h-4" />
                {t.home}
              </Link>
              <LanguageSwitcher />
              <a href="tel:+4793922819">
                <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">
                  <Phone className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t.callNowWithNumber}</span>
                </Button>
              </a>
            </div>
          </div>
        </nav>

        {/* PROMO BANNER */}
        {banner && (
          <div 
            className="fixed top-14 left-0 right-0 z-40 py-2 px-4 shadow-lg"
            style={{ 
              background: `linear-gradient(90deg, ${banner.background_color}, ${banner.background_color}CC, ${banner.background_color})`,
              fontFamily: banner.font_family === 'unbounded' ? "'Unbounded', sans-serif" : 
                         banner.font_family === 'mono' ? "monospace" : "inherit"
            }}
          >
            <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 flex-wrap text-center">
              <span className="text-lg">🎉</span>
              <span className="font-bold" style={{ color: banner.text_color || '#050505', fontSize: banner.title_size || '14px' }}>
                {lang === "no" ? banner.title_no : banner.title_en}
              </span>
              {banner.discount_text && (
                <span className="bg-[#050505] font-black px-3 py-0.5 rounded-full" style={{ color: banner.background_color, fontSize: '14px' }}>
                  {banner.discount_text}
                </span>
              )}
              <span style={{ color: banner.text_color || '#050505', fontSize: banner.desc_size || '12px' }}>
                {lang === "no" ? banner.description_no : banner.description_en}
              </span>
              <span className="text-lg">🔥</span>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* HERO SECTION - SEO OPTIMIZED */}
        {/* ============================================ */}
        <section className={`${banner ? 'pt-36' : 'pt-24'} pb-16 px-4`}>
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Cross-link badges */}
            <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
              <Link to="/stillas-privat-oslo">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full hover:bg-[#F7931A]/20 transition-colors cursor-pointer">
                  <Home className="w-4 h-4 text-[#F7931A]" />
                  <span className="text-sm text-[#F7931A] font-medium">{t.forPrivate}</span>
                  <ChevronRight className="w-4 h-4 text-[#F7931A]" />
                </div>
              </Link>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full">
                <Building2 className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#10B981] font-medium">{t.forBusinesses}</span>
              </div>
            </div>
            
            {/* H1 - SEO OPTIMIZED TITLE */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-['Unbounded'] font-bold text-white mb-6 leading-tight">
              {t.heroTitle}
              <span className="block text-[#F7931A] mt-2">{t.heroSubtitle}</span>
            </h1>
            
            {/* SUBTITLE - keyword rich */}
            <p className="text-base sm:text-lg text-[#94A3B8] mb-8 max-w-3xl mx-auto">
              {t.heroDescription} <strong className="text-white">Doserne AS</strong>.
            </p>
            
            {/* BENEFITS - bullet points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 max-w-2xl mx-auto text-left">
              {[
                t.benefit1,
                t.benefit2,
                t.benefit3,
                t.trust6
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-white">
                  <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#F7931A] hover:bg-[#E8820F] text-white font-bold px-8 py-6 text-lg shadow-lg shadow-[#F7931A]/30"
                onClick={() => document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-quote"
              >
                <FileText className="w-5 h-5 mr-2" />
                {t.getQuote}
              </Button>
              <a href="tel:+4793922819">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 font-bold px-8 py-6 text-lg w-full"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {t.callNow} 93 92 28 19
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* SECTION 2 - FOR HVEM */}
        {/* ============================================ */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#10B981]/5 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-4">
                🏢 {lang === "no" ? "For bedrifter som trenger pålitelig stillas" : "For businesses that need reliable scaffolding"}
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                {lang === "no" 
                  ? "Vi samarbeider med entreprenører, håndverkere og eiendomsutviklere som trenger stabile og effektive stillasløsninger."
                  : "We work with contractors, craftsmen and property developers who need stable and efficient scaffolding solutions."}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Building2, text: lang === "no" ? "Entreprenører og byggfirma" : "Contractors and construction companies" },
                { icon: Wrench, text: lang === "no" ? "Fasade- og malerarbeid" : "Facade and painting work" },
                { icon: HardHat, text: lang === "no" ? "Takarbeid og rehabilitering" : "Roofing and rehabilitation" },
                { icon: Shield, text: lang === "no" ? "Prosjekter med krav til fremdrift og sikkerhet" : "Projects requiring progress and safety" }
              ].map((item, i) => (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#10B981]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <span className="text-white">{item.text}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* PROJECT GALLERY - Moved up for better engagement */}
        {/* ============================================ */}
        <section className="py-12 px-4 bg-[#0A0A0A] overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white mb-3 flex items-center justify-center gap-2">
                <Camera className="w-6 h-6 text-[#F7931A]" />
                {t.projectsTitle}
              </h2>
              <p className="text-[#94A3B8] text-sm">
                {lang === "no" 
                  ? <>Vi har levert <strong className="text-white">stillasløsninger</strong> til en rekke prosjekter i Oslo</>
                  : <>We have delivered <strong className="text-white">scaffolding solutions</strong> to numerous projects in Oslo</>}
              </p>
            </div>
            
            {/* Infinite Scrolling Carousel */}
            <InfiniteImageCarousel 
              images={displayImages}
              speed={35}
              pauseOnHover={true}
              className="py-2"
            />
            
            {/* Scroll hint */}
            <p className="text-center text-[#64748B] text-xs mt-3 flex items-center justify-center gap-2">
              <ChevronLeft className="w-3 h-3" />
              {t.scrollHint}
              <ChevronRight className="w-3 h-3" />
            </p>
          </div>
        </section>

        {/* ============================================ */}
        {/* SECTION 3 - HVORFOR VELGE OSS */}
        {/* ============================================ */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-10 text-center">
              🤝 {t.trustTitle}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Award, text: t.trust1, highlight: true },
                { icon: MapPin, text: t.trust2 },
                { icon: Users, text: t.trust3 },
                { icon: Wrench, text: t.trust4 },
                { icon: Shield, text: t.trust5 },
                { icon: Clock, text: t.trust6 }
              ].map((item, i) => (
                <div key={i} className={`flex items-start gap-3 p-4 rounded-lg border ${item.highlight ? 'bg-[#F7931A]/10 border-[#F7931A]/30' : 'bg-white/5 border-white/10'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${item.highlight ? 'bg-[#F7931A]/20' : 'bg-[#23F7DD]/20'}`}>
                    <item.icon className={`w-4 h-4 ${item.highlight ? 'text-[#F7931A]' : 'text-[#23F7DD]'}`} />
                  </div>
                  <span className="text-white text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* SECTION 4 - TRUST (HVEM STÅR BAK) */}
        {/* ============================================ */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-[#10B981]/10 to-white/5 border-[#10B981]/20">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#10B981]/20 rounded-full flex items-center justify-center">
                    <HardHat className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white">
                    {lang === "no" ? "Hvem står bak AllStillas?" : "Who's behind AllStillas?"}
                  </h2>
                </div>
                
                <p className="text-[#94A3B8] mb-4">
                  <strong className="text-white">AllStillas</strong> {lang === "no" 
                    ? "er en tjeneste levert av" 
                    : "is a service delivered by"} <strong className="text-[#10B981]">Doserne AS</strong> – {lang === "no"
                    ? "et etablert stillasfirma med solid erfaring fra byggeprosjekter i Oslo og Viken."
                    : "an established scaffolding company with solid experience from construction projects in Oslo and Viken."}
                </p>
                
                <p className="text-[#94A3B8]">
                  {lang === "no"
                    ? <>Vi kombinerer praktisk erfaring med moderne løsninger for å sikre trygge og effektive prosjekter. Som lokalt <strong className="text-white">stillasfirma i Oslo</strong> kan vi tilby rask respons og fleksible løsninger.</>
                    : <>We combine practical experience with modern solutions to ensure safe and efficient projects. As a local <strong className="text-white">scaffolding company in Oslo</strong>, we can offer quick response and flexible solutions.</>}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ============================================ */}
        {/* QUOTE FORM - SIMPLIFIED */}
        {/* ============================================ */}
        <section id="quote-form" className="py-16 px-4 bg-gradient-to-b from-[#10B981]/10 to-[#0A0A0A]">
          <div className="max-w-lg mx-auto">
            <Card className="bg-[#0A0A0A] border-[#10B981]/30">
              <CardContent className="p-6 sm:p-8">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{t.thankYou}</h3>
                    <p className="text-[#94A3B8]">{t.willContact}</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white mb-2 text-center">
                      📋 {t.formTitle}
                    </h3>
                    <p className="text-[#94A3B8] text-sm text-center mb-6">
                      {t.formSubtitle}
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Row 1: Firmanavn + Kontaktperson */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[#94A3B8]">{t.companyName}</Label>
                          <Input
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            className="bg-white/5 border-white/20 text-white mt-1"
                            placeholder={t.companyPlaceholder}
                          />
                        </div>
                        <div>
                          <Label className="text-[#94A3B8]">{t.contactPerson} *</Label>
                          <Input
                            value={formData.contact_person}
                            onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                            className="bg-white/5 border-white/20 text-white mt-1"
                            placeholder={t.contactPlaceholder}
                            required
                          />
                        </div>
                      </div>
                      
                      {/* Prosjektadresse */}
                      <div>
                        <Label className="text-[#94A3B8]">{t.projectAddress}</Label>
                        <Input
                          value={formData.project_address}
                          onChange={(e) => setFormData({ ...formData, project_address: e.target.value })}
                          className="bg-white/5 border-white/20 text-white mt-1"
                          placeholder={t.addressPlaceholder}
                        />
                      </div>
                      
                      {/* Beskrivelse */}
                      <div>
                        <Label className="text-[#94A3B8]">{t.projectDescription}</Label>
                        <Textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="bg-white/5 border-white/20 text-white mt-1"
                          rows={4}
                          placeholder={t.descriptionPlaceholder}
                        />
                      </div>
                      
                      {/* Row 2: Telefon + E-post */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[#94A3B8]">{t.phone} *</Label>
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="bg-white/5 border-white/20 text-white mt-1"
                            placeholder={t.phonePlaceholder}
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-[#94A3B8]">{t.email}</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="bg-white/5 border-white/20 text-white mt-1"
                            placeholder={t.emailPlaceholder}
                          />
                        </div>
                      </div>
                      
                      {/* File Upload Section - Images & PDF */}
                      <div>
                        <Label className="text-[#94A3B8]">
                          {t.attachFiles}
                        </Label>
                        <div className="mt-2">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#F7931A]/50 hover:bg-[#F7931A]/5 transition-all">
                            <ImageIcon className="w-5 h-5 text-[#F7931A]" />
                            <span className="text-[#94A3B8] text-sm">
                              {uploading 
                                ? t.uploading
                                : `${t.selectFiles} (${t.maxFiles}, ${20 - images.length} ${t.remaining})`}
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf,application/pdf"
                              multiple
                              onChange={handleImageUpload}
                              className="hidden"
                              disabled={uploading || images.length >= 20}
                            />
                          </label>
                          
                          {/* File Preview Grid */}
                          {images.length > 0 && (
                            <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2">
                              {images.map((url, index) => (
                                <div key={index} className="relative aspect-square group">
                                  {url.includes('/raw/upload/') || url.toLowerCase().endsWith('.pdf') ? (
                                    <div className="w-full h-full bg-white/10 rounded-lg flex flex-col items-center justify-center">
                                      <FileText className="w-8 h-8 text-red-400" />
                                      <span className="text-[10px] text-[#94A3B8] mt-1">PDF</span>
                                    </div>
                                  ) : (
                                    <img 
                                      src={url} 
                                      alt={`Upload ${index + 1}`} 
                                      className="w-full h-full object-cover rounded-lg"
                                    />
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3 text-white" />
                                  </button>
                                  <div className="absolute bottom-0.5 left-0.5 bg-black/70 text-white text-[10px] px-1 rounded">
                                    {index + 1}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#F7931A] hover:bg-[#E8820F] text-white font-bold py-6 text-lg shadow-lg shadow-[#F7931A]/30"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t.sending}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            {t.submitButton}
                          </span>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ============================================ */}
        {/* FAQ SECTION - SEO OPTIMIZED */}
        {/* ============================================ */}
        <section className="py-16 px-4 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-8 text-center">
              ❓ {t.faqTitle}
            </h2>
            
            <div className="space-y-4">
              {[
                { q: t.faq1Q, a: t.faq1A },
                { q: t.faq2Q, a: t.faq2A },
                { q: t.faq3Q, a: t.faq3A },
                { q: t.faq4Q, a: t.faq4A },
                { q: t.faq5Q, a: t.faq5A }
              ].map((faq, i) => (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                      <span className="text-[#10B981]">?</span>
                      {faq.q}
                    </h3>
                    <p className="text-[#94A3B8]">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS SECTION */}
        <TestimonialsSection
          title={lang === "no" ? "Hva bedriftskundene våre sier" : "What our business customers say"}
          subtitle={lang === "no" ? "Ekte tilbakemeldinger fra fornøyde bedrifter" : "Real feedback from happy businesses"}
          variant="dark"
        />

        {/* ============================================ */}
        {/* FOOTER */}
        {/* ============================================ */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#64748B] text-sm mb-2">
              AllStillas – <strong className="text-white">{lang === "no" ? "Stillas Oslo" : "Scaffolding Oslo"}</strong> {lang === "no" ? "levert av" : "delivered by"} <span className="text-[#10B981]">Doserne AS</span>
            </p>
            <p className="text-[#64748B] text-xs mb-2">
              {lang === "no" 
                ? "Stillas for bedrifter • Leie stillas Oslo • Stillas montering Oslo og Viken"
                : "Scaffolding for businesses • Rent scaffolding Oslo • Scaffolding assembly Oslo and Viken"}
            </p>
            <p className="text-[#64748B] text-xs">
              © {new Date().getFullYear()} Doserne AS. {t.allRightsReserved}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
